<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { createDemoAccount } from '../services/demo.service'
import type { DemoCreatePayload } from '../services/demo.service'

type Language = DemoCreatePayload['language']

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()

const form = ref<DemoCreatePayload>({
  first_name: '',
  email: '',
  language: 'pl',
})

const loading = ref(false)
const errorMessage = ref<string | null>(null)

const languages: { value: Language; label: string }[] = [
  { value: 'pl', label: 'Polski' },
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
  { value: 'cs', label: 'Čeština' },
  { value: 'hu', label: 'Magyar' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'fi', label: 'Suomi' },
  { value: 'da', label: 'Dansk' },
  { value: 'sv', label: 'Svenska' },
  { value: 'no', label: 'Norsk' },
  { value: 'lt', label: 'Lietuvių' },
  { value: 'hr', label: 'Hrvatski' },
  { value: 'ro', label: 'Română' },
]

watch(() => props.modelValue, (val) => {
  if (val) {
    form.value = { first_name: '', email: '', language: 'pl' }
    errorMessage.value = null
  }
})

const close = () => {
  if (!loading.value) {
    emit('update:modelValue', false)
  }
}

const handleSubmit = async () => {
  errorMessage.value = null

  if (form.value.first_name.trim().length < 2) {
    errorMessage.value = t('demo.request.errorFirstNameMin')
    return
  }

  loading.value = true
  try {
    const response = await createDemoAccount(form.value)
    authStore.setDemoToken(response.token, response.user as any)
    emit('update:modelValue', false)
    router.push('/demo-loading')
  } catch (err: any) {
    errorMessage.value = err.response?.data?.message || t('demo.request.errorGeneral')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      style="background: rgba(15, 23, 42, 0.35); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);"
      @click.self="close"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900">{{ t('demo.request.title') }}</h2>
          <button
            @click="close"
            :disabled="loading"
            class="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>

        <!-- Body -->
        <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
          <p class="text-sm text-gray-600">{{ t('demo.request.description') }}</p>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('demo.request.firstName') }} <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.first_name"
              type="text"
              :placeholder="t('demo.request.firstNamePlaceholder')"
              class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-600 text-sm"
              :disabled="loading"
              minlength="2"
              maxlength="100"
              required
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('demo.request.email') }} <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.email"
              type="email"
              placeholder="jan.kowalski@example.com"
              class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-600 text-sm"
              :disabled="loading"
              required
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('demo.request.language') }} <span class="text-red-500">*</span>
            </label>
            <select
              v-model="form.language"
              class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-600 text-sm bg-white"
              :disabled="loading"
              required
            >
              <option v-for="lang in languages" :key="lang.value" :value="lang.value">
                {{ lang.label }}
              </option>
            </select>
          </div>

          <div v-if="errorMessage" class="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
            {{ errorMessage }}
          </div>

          <div class="flex justify-end gap-3 pt-2">
            <button
              type="button"
              @click="close"
              :disabled="loading"
              class="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <svg v-if="loading" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              {{ t('demo.request.submit') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>
